import { WSPillProps } from "@wingspanhq/fe-component-library";
import { Batch, BatchStatus } from "@wingspanhq/wingspan-api-sdk";

type Args = {
  batch: Batch;
  /** `BatchSummary.progress.total` — undefined while the summary is loading. */
  itemCount?: number;
  /** `BatchSummary.progress.failed` — undefined while the summary is loading. */
  failedCount?: number;
  showIcon?: boolean;
};

/**
 * Status pill for a payable-import batch.
 *
 * Two states have to be derived rather than read off `Batch.status`:
 *
 * - A `Created` batch is "Empty" until rows have been uploaded into it and
 *   "Awaiting import" once they have.
 * - The v3 status enum has no `PartiallyCompleted` member (the API design doc
 *   specifies one; the shipped enum is `Created | Pending | Processing |
 *   Complete | Failed`), so a batch where some rows failed still reports
 *   `Complete`. Reporting that as an unqualified success would hide the
 *   failures, so the failed count decides.
 */
export const selectorPayableImportBatchStatusPill = (
  args: Args
): WSPillProps => {
  const icon = (name: WSPillProps["icon"]) =>
    args.showIcon ? { icon: name } : {};

  switch (args.batch.status) {
    case BatchStatus.COMPLETED:
      return args.failedCount
        ? { theme: "warning", text: "Partially imported", ...icon("alert") }
        : { theme: "success", text: "Complete", ...icon("check-circle") };
    case BatchStatus.FAILED:
      return { theme: "error", text: "Import failed", ...icon("alert-circle") };
    case BatchStatus.PENDING:
      return { theme: "blue", text: "Import queued", ...icon("time") };
    case BatchStatus.PROCESSING:
      return { theme: "blue", text: "Importing", ...icon("time") };
    case BatchStatus.CREATED:
    default:
      if (args.itemCount === undefined) {
        return { theme: "neutral", text: "--", ...icon("loader") };
      }
      return args.itemCount > 0
        ? { theme: "warning", text: "Awaiting import", ...icon("alert") }
        : { theme: "neutral", text: "Empty", ...icon("block") };
  }
};
